import { z } from 'zod';
import { MAX_PROFILE_BLOCK_MESSAGE_LENGTH, ROOM_CODE_RE } from './config';
import { profileBlockFeaturesSchema } from './profile-blocks';
import {
  avatarSchema,
  configSchema,
  dailyDateSchema,
  modeSchema,
  playerNameSchema,
  textSchema as text,
} from './schema-primitives';
import { roundPlayerResultSchema, roundResultsSchema } from './results';
import { rankSchema, RANKED_TIERS } from './ranked';

const pageSchema = <T extends z.ZodType>(row: T) =>
  z.object({ rows: z.array(row).max(50), nextOffset: z.number().int().nonnegative().nullable() });

/**
 * The server-owned identity behind a credential. `name`/`avatar` are null until onboarding
 * writes them; rooms read them at join and archives snapshot them per round.
 */
export const profileSchema = z.object({
  profileId: text,
  name: text.nullable(),
  avatar: avatarSchema.nullable(),
  /** Future epoch milliseconds while play is disabled; null when play is allowed. */
  blockedUntil: z.number().int().positive().nullable(),
  /** Operator-authored plain text shown only while the block is active. */
  blockMessage: z.string().max(MAX_PROFILE_BLOCK_MESSAGE_LENGTH).nullable(),
  /** NULL restricts the whole site; [] deliberately restricts no features. */
  blockedFeatures: profileBlockFeaturesSchema.nullable(),
});
export type Profile = z.infer<typeof profileSchema>;

/** `POST`/`PUT /api/profile` body: the fields to write; absent fields are kept. */
export const profilePatchSchema = z.object({
  name: playerNameSchema.optional(),
  avatar: avatarSchema.optional(),
});
export type ProfilePatch = z.infer<typeof profilePatchSchema>;

/** `POST /api/profile` returns the credential exactly once. */
export const profileResponseSchema = profileSchema.extend({
  credential: z.string().min(1).max(256),
});
export type ProfileResponse = z.infer<typeof profileResponseSchema>;

export const dailyStatusSchema = z.object({
  roomCode: z.string().regex(ROOM_CODE_RE).nullable(),
  completed: z.boolean(),
  roundId: text.nullable(),
  score: z.number().nullable().optional(),
});

/** `POST /api/rooms` body; `config` is validated per mode by `configForMode`. */
export const createRoomRequestSchema = z.object({
  mode: modeSchema.default('versus'),
  config: z.unknown().optional(),
  // A malformed cancellation token degrades to "no token" rather than failing creation.
  cancelToken: z.string().min(32).max(128).optional().catch(undefined),
});

/** Room creation and lookup return the same public contract. */
export const roomResponseSchema = z.object({
  code: z.string().regex(ROOM_CODE_RE),
  config: configSchema,
  mode: modeSchema,
  dailyDate: dailyDateSchema.optional(),
  url: z.string().max(512),
  archiveRoundId: text.nullable().optional(),
});
export type RoomResponse = z.infer<typeof roomResponseSchema>;

/** One async-pace room the profile is a member of, as projected to D1 by the room. */
const roomMembershipSchema = z.object({
  code: z.string().regex(ROOM_CODE_RE),
  round: z.number().int().nonnegative(),
  phase: z.enum(['lobby', 'countdown', 'playing', 'results']),
  members: z.number().int().nonnegative(),
  /** True while this profile owes a turn, or while a completed Turns round awaits Next round. */
  needsAction: z.boolean(),
  updatedAt: z.number(),
});
export const myRoomsResponseSchema = z.object({ rooms: z.array(roomMembershipSchema).max(50) });
export type RoomMembership = z.infer<typeof roomMembershipSchema>;
export type MyRoomsResponse = z.infer<typeof myRoomsResponseSchema>;

const archivedResultSchema = roundResultsSchema.omit({ players: true, missed: true }).extend({
  roomCode: text,
  mode: modeSchema,
  dailyDate: dailyDateSchema.optional(),
  score: roundPlayerResultSchema.shape.score,
  total: roundPlayerResultSchema.shape.total,
  words: roundPlayerResultSchema.shape.words,
});
export const historyResponseSchema = pageSchema(archivedResultSchema);
export type HistoryResponse = z.infer<typeof historyResponseSchema>;

/** The viewer's place on a Daily's leaderboard when read: tied best scores share a rank. */
const dailyStandingSchema = z.object({ rank: z.number().int().positive() });
export type DailyStanding = z.infer<typeof dailyStandingSchema>;

/**
 * Detailed completed-round response for `GET /api/results/:roundId`: the viewer's personal
 * archive fields (`score`/`total`/`words`, shown as the summary line) plus full standings
 * (`players`/`missed`/`viewerId`) for the post-match body. `missed` is null when the round
 * was archived before missed words were stored.
 */
export const resultDetailSchema = archivedResultSchema.extend({
  players: roundResultsSchema.shape.players,
  missed: roundResultsSchema.shape.missed.nullable(),
  viewerId: text,
  /** Daily only, once the viewer is on that day's leaderboard; absent otherwise and from older servers. */
  dailyStanding: dailyStandingSchema.optional(),
});
export type ResultDetail = z.infer<typeof resultDetailSchema>;

const leaderboardRowSchema = z.object({
  profileId: text,
  name: text,
  avatar: text,
  bestScore: z.number(),
  rounds: z.number(),
});
export const leaderboardResponseSchema = pageSchema(leaderboardRowSchema);
export type LeaderboardRow = z.infer<typeof leaderboardRowSchema>;
export type LeaderboardResponse = z.infer<typeof leaderboardResponseSchema>;

const scoreboardRowSchema = leaderboardRowSchema.extend({ totalScore: z.number() });
export const scoreboardResponseSchema = pageSchema(scoreboardRowSchema);
export type ScoreboardResponse = z.infer<typeof scoreboardResponseSchema>;

const matchRecordSchema = z.object({
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  draws: z.number().int().nonnegative(),
});

/** `GET /api/ranked`: the credential's visible rank; hidden MMR never leaves the server. */
export const rankedProfileResponseSchema = matchRecordSchema.extend({ rank: rankSchema });
export type RankedProfileResponse = z.infer<typeof rankedProfileResponseSchema>;

/** `GET /api/ladder`: placed players by tier, then points. Identity is the profile's current one. */
const ladderRowSchema = matchRecordSchema.extend({
  profileId: text,
  name: text,
  avatar: text,
  tier: z
    .number()
    .int()
    .min(0)
    .max(RANKED_TIERS.length - 1),
  points: z.number().int().nonnegative(),
});
export const ladderResponseSchema = pageSchema(ladderRowSchema);
export type LadderRow = z.infer<typeof ladderRowSchema>;
export type LadderResponse = z.infer<typeof ladderResponseSchema>;
